import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { KpopCategoryNav } from "@/components/KpopCategoryNav";
import { Music, Users, Heart, ArrowRight, Sparkles, Radio, Play, ExternalLink } from "lucide-react";

const StudyWithGOT7 = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Study With GOT7 - GOT7 Study Rooms"
        description="Study with GOT7 fans worldwide. Join Ahgases in virtual study rooms with GOT7 playlists, Pomodoro timer, and productivity tools. Stay focused, stay motivated."
        keywords="study with got7, got7 study room, ahgase study, igot7 study, kpop study room, got7 playlist study"
        canonicalUrl="/study-with-got7/"
      />
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-background via-muted to-background" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} />
        
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-5xl mx-auto text-center space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 animate-fade-in">
              <Music className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold text-primary">CSW STUDY TOGETHER</span>
            </div>
            
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight animate-fade-in" style={{ animationDelay: '0.1s' }}>
              Study With{" "}
              <span className="text-gradient">GOT7</span>
            </h1>
            
            <p className="text-xl sm:text-2xl text-muted-foreground max-w-3xl mx-auto font-light animate-fade-in" style={{ animationDelay: '0.2s' }}>
              Join a global community of Ahgases who combine their passion for GOT7 with productive study sessions. Stay focused, stay motivated, and study together.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4 animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <Button 
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                asChild
              >
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  Start A Room Now <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </Button>
            </div>
            
            <div className="relative mt-12 animate-fade-in" style={{ animationDelay: '0.4s' }}>
              <div className="relative rounded-2xl overflow-hidden border-2 border-border/50 bg-card/30 backdrop-blur-sm shadow-2xl">
                <video 
                  autoPlay 
                  loop 
                  muted 
                  playsInline
                  className="w-full aspect-video object-cover"
                >
                  <source src="/videos/productivity-demo.mp4" type="video/mp4" />
                </video>
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/90 flex items-center justify-center glow-primary">
                    <Play className="w-5 h-5 text-primary-foreground ml-1" />
                  </div>
                  <span className="text-foreground font-medium">See how it works</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About GOT7 */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto space-y-8">
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground text-center">
              Study with <span className="text-gradient">GOT7</span>
            </h2>
            <Card className="p-8 sm:p-12 bg-card/50 backdrop-blur-sm border-border/50">
              <div className="space-y-6 text-muted-foreground leading-relaxed text-lg">
                <p>
                  <a href="https://got7.jype.com/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">GOT7</a> is a South Korean boy band that debuted in 2014. Known for their martial arts tricking, impressive performances, and diverse musical style, GOT7 consists of seven multi-talented members: JAY B, Mark, Jackson, Jinyoung, Youngjae, BamBam, and Yugyeom.
                </p>
                <p>
                  GOT7's fanbase, known as Ahgase (also IGOT7), has remained devoted throughout the years. Follow them on <a href="https://www.instagram.com/got7.official/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Instagram</a>, <a href="https://twitter.com/GOT7Official" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">X (Twitter)</a>, and <a href="https://www.youtube.com/@GOT7" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">YouTube</a>.
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto space-y-12">
            <div className="text-center space-y-4">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">
                CSW: The Ultimate Platform to <span className="text-gradient">Study with GOT7</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {[
                { icon: <Music className="w-7 h-7" />, title: "GOT7 Study Playlists", desc: "Curated playlists featuring GOT7's diverse discography to keep you motivated." },
                { icon: <Users className="w-7 h-7" />, title: "Connect with Ahgase", desc: "Meet fellow Ahgases from around the world who share your passion." },
                { icon: <Sparkles className="w-7 h-7" />, title: "Fly-Themed Rooms", desc: "Study rooms with GOT7-inspired aesthetics and energy." },
                { icon: <Radio className="w-7 h-7" />, title: "7 for 7 Sessions", desc: "Study with dedication like GOT7's bond with Ahgase." },
              ].map((feature, index) => (
                <Card key={index} className="p-8 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 animate-fade-in" style={{ animationDelay: `${0.1 * index}s` }}>
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center text-primary mb-6">
                    {feature.icon}
                  </div>
                  <h3 className="font-display text-2xl font-semibold text-foreground mb-4">{feature.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{feature.desc}</p>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Official Links */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute top-1/2 right-0 w-72 h-72 bg-primary/10 rounded-full blur-[100px]" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto text-center space-y-6">
            <h3 className="font-display text-2xl font-semibold text-foreground">Official GOT7 Resources</h3>
            <div className="flex flex-wrap justify-center gap-4">
              {[
                { name: "YouTube", url: "https://www.youtube.com/@GOT7" },
                { name: "Instagram", url: "https://www.instagram.com/got7.official/" },
                { name: "X (Twitter)", url: "https://twitter.com/GOT7Official" },
                { name: "Spotify", url: "https://open.spotify.com/artist/0XATRDCYuuGhk0oE7C0o5G" },
              ].map((link) => (
                <a 
                  key={link.name}
                  href={link.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card border border-border/50 hover:border-primary/50 hover:bg-primary/10 transition-all text-sm font-medium text-foreground"
                >
                  {link.name}
                  <ExternalLink className="w-3 h-3 text-muted-foreground" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* YouTube Videos Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute top-1/2 right-0 w-96 h-96 bg-primary/10 rounded-full blur-[150px]" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto space-y-12">
            <div className="text-center space-y-4">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">
                Watch <span className="text-gradient">GOT7</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { id: "yY13X0BKaUw", title: "GOT7 - Nanana M/V" },
                { id: "IZ1t7CwfvEc", title: "GOT7 - Breath M/V" },
                { id: "aSHhEqhgT9E", title: "GOT7 - ENCORE M/V" },
                { id: "vrdk3IGcau8", title: "GOT7 - Lullaby M/V" },
                { id: "iqWPGJoq30I", title: "GOT7 - You Calling My Name M/V" },
                { id: "kRj4toENrnA", title: "GOT7 - NOT BY THE MOON M/V" },
              ].map((video, index) => (
                <Card 
                  key={video.id} 
                  className="overflow-hidden border-border/50 hover:border-primary/50 transition-all hover:scale-105 animate-fade-in bg-card/50 backdrop-blur-sm"
                  style={{ animationDelay: `${0.1 * index}s` }}
                >
                  <div className="aspect-video">
                    <iframe
                      src={`https://www.youtube.com/embed/${video.id}`}
                      title={video.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full"
                    />
                  </div>
                  <div className="p-4">
                    <p className="font-medium text-foreground text-sm">{video.title}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px]" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Ready to Study with <span className="text-gradient">GOT7?</span>
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Join thousands of Ahgases who are staying focused and motivated. Start your study session today.
            </p>
            <Button 
              size="lg"
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-12 py-6 text-lg glow-primary transition-all hover:scale-105"
              asChild
            >
              <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                Explore CSW Rooms <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </Button>
          </div>
        </div>
      </section>

      <KpopCategoryNav currentGroup="GOT7" />
      <Footer />
    </div>
  );
};

export default StudyWithGOT7;
